import * as TalkPlus from 'talkplus-sdk';
import { MessageTypeEnum, User } from 'talkplus-sdk';
import Swal from 'sweetalert2';
import { JWPlayerConfig } from '@src/modules/players/shared/interfaces/player-configs.interface';
import { check } from 'korcen';
import toastr from 'toastr';
import { getEventState } from '@root/private/js/jw/live/error-handler';
import { LIVE_EVENT_CONN_STATE } from '@src/modules/players/shared/interfaces/live-info.interface';

declare const _playerOptions: JWPlayerConfig;

const {
  x_midibus_evt_id: eventId,
  x_midibus_st_id: streamId,
  x_midibus_app_id: appId,
  x_midibus_chatting: chatType,
  x_midibus_uuid: uuid,
} = _playerOptions;

const userId = Math.floor(+new Date() / 1000) + '';
let userSetUsername;
const isChatPopup = window.location.pathname.includes('chat');
let existingMembers: string[] = [];

// talkplus 채팅방 구분 단위인 channelId는 미디버스 라이브 이벤트의 eventId로 사용한다.

const initLiveChat = async () => {
  const eventState = await getEventState(eventId);
  const showErroMessage = (message = '') => {
    document.querySelector('.chat-error').classList.remove('hidden');
    document.querySelector('.upper').classList.add('hidden');
    if (message) {
      document.querySelectorAll('.error-message p').forEach((el) => {
        document.querySelector('.error-message').removeChild(el);
      });
      message.split('/').forEach((messageContent) => {
        const pEl = document.createElement('p');
        pEl.innerText = messageContent;
        document.querySelector('.error-message').appendChild(pEl);
      });
    }
  };

  if (
    eventState.connState === LIVE_EVENT_CONN_STATE.READY ||
    eventState.connState === LIVE_EVENT_CONN_STATE.FINISHED
  ) {
    showErroMessage(
      eventState.connState === LIVE_EVENT_CONN_STATE.READY
        ? '라이브 방송이 준비중입니다.'
        : '라이브 방송이 종료되었습니다.',
    );
    (document.querySelector('.btn-chat-reload') as HTMLElement).style.display =
      'none';
    return;
  }

  const chatForm = document.querySelector('#message-form');
  const inputMessageText = document.querySelector(
    '.enterMessage',
  ) as HTMLInputElement;
  const messageBoxes = document.querySelectorAll('.message-box');
  const closeBtn = document.querySelector('.btn-close') as HTMLElement;
  const chatContainer = document.querySelector('.chat-wrap') as HTMLElement;

  closeBtn.addEventListener('click', () => {
    if (isChatPopup) {
      window.close();
    } else {
      chatContainer.classList.remove('open');
    }
  });

  if (!chatType) {
    toastr.error('채팅방에 입장할 수 없습니다. 관리자에게 문의해 주세요.');
    return;
  }
  const chatClient = new TalkPlus.Client({
    appId,
  });

  const checkChannelCreated = async () => {
    try {
      const res = await chatClient.getChannel({
        channelId: eventId,
      });
      if (res) {
        existingMembers = res.channel.members.map((member) => member.username);
      }
      return Boolean(res);
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const setChatAreaScrollToBottom = () => {
    const chatAreas = document.querySelectorAll('.chat-area');
    chatAreas.forEach((chatArea) =>
      chatArea.scrollTo(0, chatArea.scrollHeight),
    );
  };

  const addMessage = ({
    username,
    text,
    userId: messageUserId,
  }: Partial<TalkPlus.Message>) => {
    const newMessageBox = document.createElement('div');
    newMessageBox.className = 'message';
    newMessageBox.classList.add(
      'rounded',
      'border',
      username === userSetUsername ? 'sender' : 'receiver',
    );

    const divUsername = document.createElement('div');
    divUsername.className = 'username';
    divUsername.textContent = username || 'anonymous';

    const messageText = document.createElement('div');
    messageText.className = 'message-text';
    messageText.textContent = text;

    newMessageBox.appendChild(divUsername);
    newMessageBox.appendChild(messageText);

    if (messageUserId === `admin-${eventId}`) {
      newMessageBox.classList.add('admin');
      const adminMark = document.createElement('img');
      adminMark.src = `/public/images/chat-admin-mark.svg`;
      divUsername.appendChild(adminMark);
    }

    messageBoxes.forEach((box) => {
      box.appendChild(newMessageBox.cloneNode(true));
    });
    setChatAreaScrollToBottom();
  };

  let retryCount = 0;
  document.querySelector('.btn-chat-reload').addEventListener('click', () => {
    if (retryCount === 3) {
      toastr.error('채팅방에 입장할 수 없습니다. 관리자에게 문의해 주세요.');
      return;
    }
    retryCount += 1;
    initTalkPlus();
    showErroMessage();
  });

  if (!isChatPopup) {
    const btnChatOuterWindow = document.querySelector('.btn-chat-outer-window');
    const toggleChatOuterWindow = () => {
      chatContainer.classList.remove('open');

      const chatUrl = `/live/${streamId}/chat`;
      const chatPopupWindow = window.open(
        chatUrl,
        'chat-popup',
        'width=300,height=500,resizable=yes,toolbar=no,location=no,menubar=no,status=no,scrollbar=no',
      );

      if (chatPopupWindow) {
        const timer = setInterval(() => {
          if (chatPopupWindow.closed) {
            clearInterval(timer);
            chatContainer.classList.add('open');
          }
        }, 300);
      }
    };
    btnChatOuterWindow.addEventListener('click', toggleChatOuterWindow);
  }

  const initTalkPlus = async () => {
    try {
      await chatClient.loginAnonymous({
        userId,
        username: uuid,
      });

      if (!(await checkChannelCreated())) {
        document.querySelector('.upper').classList.add('hidden');
        document.querySelector('.chat-error').classList.remove('hidden');
        return;
      }

      await chatClient.joinChannel({
        channelId: eventId,
      });

      const lastMessages = await chatClient.getMessages({
        channelId: eventId,
        order: 'oldest', // default
        limit: 50,
      });

      lastMessages.messages.forEach((message) => {
        addMessage(message);
      });

      setChatAreaScrollToBottom();
    } catch (e) {
      console.error(e);
    }
  };

  const messageSendBtn = document.querySelector(
    '.btn-send',
  ) as HTMLButtonElement;

  const checkIfBlocked = async (channelMembers: User[]) => {
    if (!channelMembers.find((member) => member.id === userId)) {
      await Swal.fire({
        title: '강제 퇴장',
        text: '부적절한 사용으로 관리자에 의해 강제퇴장 당하셨습니다.',
        heightAuto: false,
        confirmButtonText: '확인',
        allowOutsideClick: false,
      });

      inputMessageText.disabled = true;
      messageSendBtn.disabled = true;
    }
  };

  (chatClient as any).on('event', (payload) => {
    if (payload.type === 'message') {
      addMessage(payload.message);
    }
    if (payload.type === 'memberBanned') {
      checkIfBlocked(payload.channel.members);
    }
  });
  const updateUsername = async () => {
    if (userSetUsername) return;

    const modalResult = await Swal.fire({
      title: '<div class="title-icon"></div><p>닉네임 설정</p>',
      text: '채팅에서 사용할 닉네임을 설정해주세요',
      input: 'text',
      inputValidator: (value) => {
        if (!value) return '유효한 닉네임을 입력해주세요.';
        if (existingMembers.includes(value))
          return '이미 사용중인 닉네임입니다.';
        if (check(value)) return '부적절한 닉네임입니다.';
        if (value.length > 128) return '128자 이내의 닉네임을 입력해주세요.';
        return '';
      },
      heightAuto: false,
      confirmButtonText: '확인',
      allowOutsideClick: true,
      confirmButtonColor: '#58c3bb',
      showCancelButton: true,
      cancelButtonText: '취소',
      inputPlaceholder: '원하는 닉네임을 입력해주세요',
      padding: '2rem',
      cancelButtonColor: 'white',
      inputAutoFocus: true,
      inputAutoTrim: true,
      customClass: { container: 'swal2-chat' },
    });

    if (!modalResult.isConfirmed) return;
    const { value: newUsername } = modalResult;

    try {
      await chatClient.updateUser({
        username: newUsername,
      });
      userSetUsername = newUsername;
    } catch (e) {
      toastr.error('닉네임 설정에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const sendMessageInputValueToTalkplus = async () => {
    const message = inputMessageText.value;
    if (!message) return;

    if (!userSetUsername) {
      toastr.info('닉네임을 먼저 설정해 주세요');
      await updateUsername();
      if (!userSetUsername) return;
    }

    // add message
    try {
      await chatClient.sendMessage({
        channelId: eventId,
        type: MessageTypeEnum.Text,
        text: message,
      });

      addMessage({
        username: userSetUsername,
        text: message,
        userId,
      });
    } catch (e) {
      toastr.error('메세지를 전송할 수 없습니다.');
    }

    // reset input box
    inputMessageText.value = '';

    setChatAreaScrollToBottom();
  };

  chatForm.addEventListener('submit', (e) => {
    e.preventDefault();
    sendMessageInputValueToTalkplus();
  });

  inputMessageText.addEventListener('click', updateUsername);

  window.addEventListener('unload', (e) => {
    e.preventDefault();

    navigator.sendBeacon(`/chat/${eventId}/leave/${userId}`);
  });

  initTalkPlus();
};

export default initLiveChat;
